import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { ButtonBase, Stack, Tooltip, Typography } from '@mui/material';
import { useLinkedinAccountsQuery } from 'features/co-pilot/linkedin-accounts/hooks/use-linkedin-accounts-query';
import { AccountProblem, getAccountProblem } from './linkedin-connection.logic';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';

interface Props {
  /** Account management page of the current product area. */
  accountsRoute: string;
}

/**
 * Hinweis in der oberen Leiste, solange ein LinkedIn-Konto nicht sauber verbunden ist —
 * auf jeder Seite, nicht nur in der Kontoverwaltung, mit der konkreten Fehlermeldung.
 */
const LinkedinConnectionBanner: React.FC<Props> = ({ accountsRoute }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: accounts } = useLinkedinAccountsQuery({ refetchInterval: 60 * 1000 });

  const problems = (accounts || [])
    .map((account) => getAccountProblem(account, t))
    .filter((problem): problem is AccountProblem => problem !== null);

  if (problems.length === 0) return null;

  const [first] = problems;
  const more = problems.length - 1;

  return (
    <Tooltip
      arrow
      placement="bottom"
      title={
        <Stack gap={0.75}>
          {problems.map((problem) => (
            <div key={problem.accountId}>
              <Typography className={classes.tooltipTitle}>
                {problem.name}: {problem.label}
              </Typography>
              {problem.detail ? <Typography className={classes.tooltipDetail}>{problem.detail}</Typography> : null}
            </div>
          ))}
        </Stack>
      }
    >
      <ButtonBase className={classes.banner} onClick={() => navigate(accountsRoute)} data-testid="linkedin-connection-banner">
        <WarningAmberRoundedIcon className={classes.icon} />
        <Typography className={classes.text} noWrap>
          {t('linkedinNotConnectedBanner')}: {first.name} — {first.label}
          {more > 0 ? ` ${t('linkedinBannerMore', { count: more } as any)}` : ''}
        </Typography>
      </ButtonBase>
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme) => ({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    maxWidth: '560px',
    height: '34px',
    padding: '0 12px',
    marginLeft: '16px',
    borderRadius: '8px',
    background: '#FFF4E5',
    border: '1px solid #FFB547',
    color: '#8A4B00',
    '&:hover': {
      background: '#FFE7C2',
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(18),
    flexShrink: 0,
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
    color: 'inherit',
  },
  tooltipTitle: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 600,
  },
  tooltipDetail: {
    fontSize: theme.typography.pxToRem(12),
    opacity: 0.85,
  },
}));

export default LinkedinConnectionBanner;
